import { Component, useState, type ReactNode } from 'react';
import { useApp } from './context.tsx';
import { supabase } from './supabase.ts';
import type { LoadState } from './useLoad.ts';

/** Renders a loading line, a failure with retry, or the loaded content. */
export function Loaded<T>(props: {
  readonly state: LoadState<T>;
  readonly reload: () => void;
  readonly children: (data: T) => ReactNode;
}) {
  const { t } = useApp();
  if (props.state.status === 'loading') return <p className="muted">{t('loading')}</p>;
  if (props.state.status === 'failed') {
    return (
      <div role="alert" className="notice error">
        <p>{t('loadFailed')}</p>
        <button type="button" className="button quiet" onClick={props.reload}>
          {t('retry')}
        </button>
      </div>
    );
  }
  return <>{props.children(props.state.data)}</>;
}

/** The emergency stop, always first on the overview: the one thing the owner must never miss. */
export function StopBand(props: { readonly killSwitch: boolean; readonly onChanged: () => void }) {
  const { t, role } = useApp();
  const [busy, setBusy] = useState(false);

  const set = async (on: boolean) => {
    if (!on && !window.confirm(t('stopConfirmRelease'))) return;
    setBusy(true);
    const { error } = await supabase.rpc('update_settings', { p_changes: { kill_switch: on } });
    setBusy(false);
    if (error) console.error(error);
    props.onChanged();
  };

  return (
    <section className={props.killSwitch ? 'stop-band stopped' : 'stop-band running'}>
      <p>{props.killSwitch ? t('stopOn') : t('stopOff')}</p>
      {role === 'owner' && (
        <button
          type="button"
          className={props.killSwitch ? 'button primary' : 'button danger'}
          disabled={busy}
          onClick={() => void set(!props.killSwitch)}
        >
          {props.killSwitch ? t('stopRelease') : t('stopEngage')}
        </button>
      )}
    </section>
  );
}

/**
 * A table that may scroll sideways on a phone. The scroll area is a labelled region that takes
 * keyboard focus, so it can be scrolled without a mouse (WCAG 2.1 "scrollable region focusable").
 */
export function ScrollTable(props: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="table-wrap" role="region" aria-label={props.label} tabIndex={0}>
      {props.children}
    </div>
  );
}

/**
 * Catches a page that fails to load (a lost connection, or a file replaced by a newer deploy)
 * so the app shows a way back instead of going blank. Reloading fetches the current files.
 */
export class PageBoundary extends Component<
  { readonly message: string; readonly retry: string; readonly children: ReactNode },
  { readonly failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError(): { failed: boolean } {
    return { failed: true };
  }

  override componentDidCatch(error: unknown): void {
    console.error(error);
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    return (
      <div role="alert" className="notice error">
        <p>{this.props.message}</p>
        <button
          type="button"
          className="button quiet"
          onClick={() => {
            window.location.reload();
          }}
        >
          {this.props.retry}
        </button>
      </div>
    );
  }
}
